import React, { useState, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { FaMobileAlt, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../Components/auth/AuthContext";
import axiosInstance from "../Components/api/axiosInstance";

const LoginPage = () => {
  const navigate = useNavigate();
  const passwordRef = useRef(null);
  const { login } = useAuth();

  const [form, setForm] = useState({ mobileNo: "", passwordHash: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  if (localStorage.getItem("token")) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNo") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, mobileNo: digitsOnly }));
      if (digitsOnly.length === 10) passwordRef.current?.focus();
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    let newErrors = {};
    if (form.mobileNo.length !== 10) newErrors.mobileNo = "Enter valid 10-digit mobile number";
    if (!form.passwordHash.trim()) newErrors.passwordHash = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const res = await axiosInstance.post("/Auth/login", {
        mobileNo: form.mobileNo,
        passwordHash: form.passwordHash,
      });

      login(res.data); // ✅ store token & user in context
      navigate("/dashboard");
    } catch (err) {
      console.error("Login failed:", err);
      alert(err.response?.data?.message || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8 border border-white/20">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <div className="bg-blue-600 p-3 rounded-full shadow-lg">
              <FaLock className="text-2xl text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">BillTrace</h1>
          <p className="text-blue-600 font-semibold text-sm">Attendance Management System</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-sm font-medium text-gray-700">Mobile Number</label>
            <div className="mt-1 relative">
              <FaMobileAlt className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                name="mobileNo"
                value={form.mobileNo}
                onChange={handleChange}
                placeholder="Enter 10-digit mobile"
                inputMode="numeric"
                maxLength={10}
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            {errors.mobileNo && <p className="text-red-500 text-xs mt-1">{errors.mobileNo}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Password</label>
            <div className="mt-1 relative">
              <FaLock className="absolute left-3 top-3 text-gray-400" />
              <input
                ref={passwordRef}
                type={showPassword ? "text" : "password"}
                name="passwordHash"
                value={form.passwordHash}
                onChange={handleChange}
                placeholder="Enter password"
                className="w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-2.5 text-gray-500 hover:text-blue-600"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.passwordHash && <p className="text-red-500 text-xs mt-1">{errors.passwordHash}</p>}
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-2.5 rounded-lg transition duration-200 shadow-lg"
          >
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;